import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Iter 48 — one-click "Late return" surcharge for vendors/admins on the rental agreement.
 *
 * The operator types the actual drop-off time; the backend computes the late minutes
 * vs the agreed `end_date`, applies the car's grace, and bills extra full days at the
 * car's `late_return_extra_day_fee` (or `price_per_day` when null). `force_days` lets
 * the operator manually override (e.g. zero-out for goodwill).
 *
 * The component is rendered only for vendor/admin/agent roles — customers see the
 * surcharge once it's been applied (rendered as a line on the agreement / invoice).
 */
export default function LateReturnPanel({ booking, role, onApplied }) {
  const [open, setOpen] = useState(false);
  const [actual, setActual] = useState(() => {
    // Default to "now" in the user's local timezone, formatted for datetime-local.
    const now = new Date();
    const tz = now.getTimezoneOffset() * 60000;
    return new Date(now - tz).toISOString().slice(0, 16);
  });
  const [forceDays, setForceDays] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const surcharge = booking.late_return_surcharge;
  const canEdit = ["vendor", "admin", "agent"].includes(role);

  const apply = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/late-return-surcharge`, {
        actual_return_at: new Date(actual).toISOString(),
        force_days: forceDays === "" ? undefined : Number(forceDays),
        note: note || undefined,
      });
      if (data.within_grace) {
        toast.success(`Within grace (${data.late_minutes}m late). No surcharge applied.`);
      } else {
        toast.success(`Surcharge applied: ${data.extra_days} extra day(s), €${data.extra_fee.toFixed(2)}.`);
      }
      setOpen(false);
      onApplied?.(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  // Render the existing surcharge summary on every viewer (customer included).
  const SurchargeRow = surcharge && (
    <div
      className={`rounded-2xl border px-4 py-3 ${surcharge.within_grace ? "border-neutral-200 bg-neutral-50" : "border-amber-200 bg-amber-50"}`}
      data-testid="late-return-summary"
    >
      <div className="flex items-center gap-2 mb-1">
        <Clock className={`h-4 w-4 ${surcharge.within_grace ? "text-neutral-500" : "text-amber-700"}`} />
        <div className="text-sm font-semibold">
          {surcharge.within_grace ? "Late return (within grace)" : "Late-return surcharge"}
        </div>
      </div>
      <div className="text-xs text-neutral-700">
        Returned <span className="font-medium">{new Date(surcharge.actual_return_at).toLocaleString()}</span>
        {" — "}
        late by {surcharge.late_minutes} min (grace {surcharge.grace_minutes} min).
      </div>
      {!surcharge.within_grace && (
        <div className="text-xs text-amber-900 mt-1" data-testid="late-return-fee-line">
          + {surcharge.extra_days} extra day{surcharge.extra_days === 1 ? "" : "s"} × €{surcharge.extra_fee_per_day.toFixed(2)} =
          {" "}<span className="font-semibold">€{surcharge.extra_fee.toFixed(2)}</span>
        </div>
      )}
      {surcharge.note && <div className="text-[11px] text-neutral-500 italic mt-1">"{surcharge.note}"</div>}
    </div>
  );

  if (!canEdit && !surcharge) return null;

  return (
    <div className="my-4 print:break-inside-avoid" data-testid="late-return-panel">
      {SurchargeRow}
      {canEdit && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full mt-2 print:hidden"
              data-testid="late-return-open-btn"
            >
              <Clock className="h-3.5 w-3.5 mr-1.5" />
              {surcharge ? "Edit late-return record" : "Mark late return"}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Late-return surcharge</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              <p className="text-xs text-neutral-500">
                Agreed return: <span className="font-medium text-neutral-700">{new Date(booking.end_date).toLocaleString()}</span>.
                Enter the actual drop-off datetime — we'll compute extra days automatically.
              </p>
              <div>
                <Label className="text-xs">Actual return datetime</Label>
                <Input
                  type="datetime-local"
                  value={actual}
                  onChange={(e) => setActual(e.target.value)}
                  className="mt-1 rounded-xl"
                  data-testid="late-return-actual"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Override extra days</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={forceDays}
                    onChange={(e) => setForceDays(e.target.value)}
                    placeholder="auto"
                    className="mt-1 rounded-xl"
                    data-testid="late-return-force-days"
                  />
                  <p className="text-[10px] text-neutral-500 mt-1">Leave blank for the auto-calc.</p>
                </div>
                <div>
                  <Label className="text-xs">Note (optional)</Label>
                  <Input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Goodwill — 1h late"
                    className="mt-1 rounded-xl"
                    data-testid="late-return-note"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
              <Button onClick={apply} disabled={saving} className="rounded-full" data-testid="late-return-apply">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Apply
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
